// Translation strings for GlobalVyapar
// Languages: English (en), Hindi (hi), Gujarati (gu)

export const translations = {
  en: {
    // Common
    common: {
      loading: "Loading...",
      save: "Save",
      cancel: "Cancel",
      submit: "Submit",
      delete: "Delete",
      edit: "Edit",
      search: "Search",
      filter: "Filter",
      all: "All",
      yes: "Yes",
      no: "No",
      or: "Or",
      and: "And",
      back: "Back",
      next: "Next",
      close: "Close",
      refresh: "Refresh",
      viewAll: "View All",
      learnMore: "Learn More",
      getStarted: "Get Started",
    },

    // Navigation
    nav: {
      dashboard: "Dashboard",
      products: "Products",
      inquiries: "Inquiries",
      leads: "Leads",
      aiDrafts: "AI Drafts",
      trends: "Trends",
      followUps: "Follow-ups",
      settings: "Settings",
      profile: "Profile",
      logout: "Logout",
      login: "Login",
      register: "Register",
      marketplace: "Marketplace",
      support: "Support",
    },

    // Auth
    auth: {
      welcome: "Welcome",
      signIn: "Sign In",
      signUp: "Sign Up",
      createAccount: "Create Account",
      email: "Email",
      password: "Password",
      confirmPassword: "Confirm Password",
      fullName: "Full Name",
      companyName: "Company Name",
      country: "Country",
      role: "I am a",
      buyer: "Buyer (Importer)",
      seller: "Seller (Exporter)",
      forgotPassword: "Forgot Password?",
      resetPassword: "Reset Password",
      newPassword: "New Password",
      sendResetLink: "Send Reset Link",
      backToLogin: "Back to Login",
      passwordResetSuccess: "Password Reset Successful!",
      passwordResetSuccessMsg: "Your password has been reset. You can now login with your new password.",
      goToLogin: "Go to Login",
      signingIn: "Signing in...",
      creatingAccount: "Creating account...",
      alreadyHaveAccount: "Already have an account?",
      dontHaveAccount: "Don't have an account?",
      tagline: "Connect. Automate. Trade Globally.",
      valueProposition: "Already getting 10+ inquiries/week but converting <20%?",
      valuePropositionAnswer: "Our system fixes that.",
    },

    // Landing Page
    landing: {
      heroTitle: "AI-Powered B2B Trade Platform",
      heroSubtitle: "Connect with verified suppliers and buyers worldwide",
      exploreMarketplace: "Explore Marketplace",
      joinFree: "Join Free",
      trustedBy: "Trusted by businesses across India",
      features: "Features",
      pricing: "Pricing",
      freeDuringBeta: "Free During Beta",
      earlyAccessOpen: "Early Access Open",
      howItWorks: "How It Works",
      forBuyers: "For Buyers",
      forSellers: "For Sellers",
      securityBadges: "Your Data is Safe",
      ssl256bit: "256-bit SSL",
      indiaServers: "India Servers",
      privacyFirst: "Privacy First",
      freeBeta: "Free Beta",
    },

    // Dashboard
    dashboard: {
      title: "Dashboard",
      overview: "Overview",
      totalProducts: "Total Products",
      totalInquiries: "Total Inquiries",
      activeLeads: "Active Leads",
      activeDeals: "Active Deals",
      recentActivity: "Recent Activity",
      noActivity: "No recent activity",
      aiFeatures: "AI Features",
      smartMatch: "AI Smart Match",
      smartMatchDesc: "Find products using natural language",
    },

    // Products
    products: {
      myProducts: "My Products",
      productMarketplace: "Product Marketplace",
      addProduct: "Add Product",
      addNewProduct: "Add New Product",
      productImages: "Product Images",
      productTitle: "Product Title",
      description: "Description",
      category: "Category",
      price: "Price",
      priceUSD: "Price (USD)",
      minOrder: "Min Order",
      unit: "Unit",
      originCountry: "Origin Country",
      hsCode: "HS Code",
      specifications: "Specifications",
      addSpecification: "Add Specification",
      aiGenerate: "AI Generate",
      generating: "Generating...",
      firstImageMain: "First image will be the main display image",
      maxSizePerImage: "Max 5MB each, JPG/PNG recommended",
      imagesAdded: "images added",
      sendInquiry: "Send Inquiry",
      verified: "Verified",
      seller: "Seller",
      manageListings: "Manage your product listings",
      discoverProducts: "Discover products from verified suppliers worldwide",
    },

    // Inquiries
    inquiries: {
      myInquiries: "My Inquiries",
      receivedInquiries: "Received Inquiries",
      trackInquiries: "Track your product inquiries and continue negotiations",
      viewAndRespond: "View and respond to buyer inquiries",
      noInquiriesSent: "No inquiries sent yet",
      noInquiriesReceived: "No inquiries received yet",
      browseProducts: "Browse products and send inquiries to get started",
      addProductsToStart: "Add products to start receiving inquiries",
      viewConversation: "View Conversation",
      initialMessage: "Initial Message",
      quantity: "Quantity",
      messages: "messages",
      typeMessage: "Type your message...",
      send: "Send",
      sending: "Sending...",
      referenceImages: "Reference Images (Optional)",
      uploadUpTo3: "Upload up to 3 images to show what you're looking for",
      addReferenceImages: "Add Reference Images",
    },

    // Leads
    leads: {
      title: "Lead Pipeline",
      subtitle: "Manage your sales pipeline",
      stages: {
        new: "New",
        contacted: "Contacted",
        negotiation: "Negotiation",
        proposal: "Proposal",
        closedWon: "Won",
        closedLost: "Lost",
      },
      noLeads: "No leads yet",
      value: "Value",
      lastContact: "Last Contact",
      moveStage: "Move Stage",
    },

    // AI Features
    ai: {
      smartMatch: "AI Smart Match",
      smartMatchPlaceholder: "Try: '4 tons of basmati rice' or 'cotton fabric 5000 meters'",
      findMatches: "Find Matches",
      matching: "Matching...",
      aiUnderstands: "AI understands quantity, quality, and finds the best sellers for you",
      smartMatchResults: "AI Smart Match Results",
      lookingFor: "Looking for",
      clearShowAll: "Clear & Show All",
      matchScore: "Match",
      recommended: "Recommended",
      whyMatches: "Why this matches",
      moqFit: "MOQ",
      generateDescription: "Generate Description",
      aiSuggestions: "AI Suggestions",
      considerAddingSpecs: "Consider adding these specs",
      keywords: "Keywords",
    },

    // Market Trends
    marketTrends: {
      title: "Market Trends & Intelligence",
      subtitle: "AI-powered insights on global trade opportunities",
      lastUpdated: "Last updated",
      refreshTrends: "Refresh Trends",
      analyzing: "Analyzing global market trends...",
      mayTakeFewSeconds: "This may take a few seconds",
      marketOverview: "Market Overview",
      trendingProducts: "Trending Products",
      trendingDesc: "Products seeing increased global demand",
      marketAlerts: "Market Alerts",
      alertsDesc: "Important news affecting trade",
      opportunities: "Opportunities",
      opportunitiesDesc: "Potential growth areas for your business",
      riskAlerts: "Risk Alerts",
      risksDesc: "Potential challenges to watch",
      priceUp: "Price up",
      priceDown: "Price down",
      priceStable: "Price stable",
      score: "Score",
      impact: "impact",
      highImpact: "high impact",
      mediumImpact: "medium impact",
      lowImpact: "low impact",
      mitigation: "Mitigation",
    },

    // Support
    support: {
      title: "Contact & Support",
      subtitle: "We're here to help you succeed",
      whatsapp: "WhatsApp (Fastest)",
      email: "Email",
      responseTime: "Response within 24 hours",
      supportHours: "Support Hours",
      faq: "Frequently Asked Questions",
    },

    // Notifications
    notifications: {
      title: "Notifications",
      markAllRead: "Mark all read",
      noNotifications: "No notifications",
      newInquiry: "New Inquiry",
      newMessage: "New Message",
      aiDraftReady: "AI Draft Ready",
    },

    // Profile
    profile: {
      title: "Profile",
      personalInfo: "Personal Information",
      companyInfo: "Company Information",
      verification: "Verification",
      verificationStatus: "Verification Status",
      verified: "Verified",
      pending: "Pending",
      unverified: "Unverified",
    },

    // Settings
    settings: {
      title: "Settings",
      knowledgeBase: "Knowledge Base",
      knowledgeBaseDesc: "Train the AI with your business information",
      language: "Language",
      selectLanguage: "Select Language",
    },

    // Footer
    footer: {
      copyright: "© 2026 GlobalVyapar. Connecting global trade.",
      privacyPolicy: "Privacy Policy",
      termsOfService: "Terms of Service",
      refundPolicy: "Refund Policy",
    },

    // Toast Messages
    toast: {
      success: "Success",
      error: "Error",
      loginSuccess: "Welcome back!",
      registerSuccess: "Account created successfully!",
      logoutSuccess: "Logged out successfully",
      productAdded: "Product added successfully",
      inquirySent: "Inquiry sent successfully!",
      messageSent: "Message sent",
      savedChanges: "Changes saved",
      failedToLoad: "Failed to load data",
      pleaseEnter: "Please enter",
      invalidCredentials: "Invalid credentials",
    },
  },

  hi: {
    // Common
    common: {
      loading: "लोड हो रहा है...",
      save: "सहेजें",
      cancel: "रद्द करें",
      submit: "जमा करें",
      delete: "हटाएं",
      edit: "संपादित करें",
      search: "खोजें",
      filter: "फ़िल्टर",
      all: "सभी",
      yes: "हाँ",
      no: "नहीं",
      or: "या",
      and: "और",
      back: "वापस",
      next: "अगला",
      close: "बंद करें",
      refresh: "रीफ्रेश",
      viewAll: "सभी देखें",
      learnMore: "और जानें",
      getStarted: "शुरू करें",
    },

    // Navigation
    nav: {
      dashboard: "डैशबोर्ड",
      products: "उत्पाद",
      inquiries: "पूछताछ",
      leads: "लीड्स",
      aiDrafts: "AI ड्राफ्ट",
      trends: "ट्रेंड्स",
      followUps: "फॉलो-अप",
      settings: "सेटिंग्स",
      profile: "प्रोफ़ाइल",
      logout: "लॉगआउट",
      login: "लॉगिन",
      register: "रजिस्टर",
      marketplace: "मार्केटप्लेस",
      support: "सहायता",
    },

    // Auth
    auth: {
      welcome: "स्वागत है",
      signIn: "साइन इन करें",
      signUp: "साइन अप करें",
      createAccount: "खाता बनाएं",
      email: "ईमेल",
      password: "पासवर्ड",
      confirmPassword: "पासवर्ड की पुष्टि करें",
      fullName: "पूरा नाम",
      companyName: "कंपनी का नाम",
      country: "देश",
      role: "मैं हूँ",
      buyer: "खरीदार (आयातक)",
      seller: "विक्रेता (निर्यातक)",
      forgotPassword: "पासवर्ड भूल गए?",
      resetPassword: "पासवर्ड रीसेट करें",
      newPassword: "नया पासवर्ड",
      sendResetLink: "रीसेट लिंक भेजें",
      backToLogin: "लॉगिन पर वापस जाएं",
      passwordResetSuccess: "पासवर्ड रीसेट सफल!",
      passwordResetSuccessMsg: "आपका पासवर्ड रीसेट हो गया है। अब आप अपने नए पासवर्ड से लॉगिन कर सकते हैं।",
      goToLogin: "लॉगिन पर जाएं",
      signingIn: "साइन इन हो रहा है...",
      creatingAccount: "खाता बना रहे हैं...",
      alreadyHaveAccount: "पहले से खाता है?",
      dontHaveAccount: "खाता नहीं है?",
      tagline: "जुड़ें। स्वचालित करें। वैश्विक व्यापार करें।",
      valueProposition: "क्या आपको 10+ पूछताछ/सप्ताह मिल रही है लेकिन <20% रूपांतरण?",
      valuePropositionAnswer: "हमारा सिस्टम इसे ठीक करता है।",
    },

    // Landing Page
    landing: {
      heroTitle: "AI-संचालित B2B व्यापार प्लेटफ़ॉर्म",
      heroSubtitle: "दुनिया भर के सत्यापित आपूर्तिकर्ताओं और खरीदारों से जुड़ें",
      exploreMarketplace: "मार्केटप्लेस देखें",
      joinFree: "मुफ्त जुड़ें",
      trustedBy: "भारत भर के व्यवसायों द्वारा विश्वसनीय",
      features: "विशेषताएं",
      pricing: "मूल्य निर्धारण",
      freeDuringBeta: "बीटा के दौरान मुफ्त",
      earlyAccessOpen: "अर्ली एक्सेस खुला है",
      howItWorks: "यह कैसे काम करता है",
      forBuyers: "खरीदारों के लिए",
      forSellers: "विक्रेताओं के लिए",
      securityBadges: "आपका डेटा सुरक्षित है",
      ssl256bit: "256-बिट SSL",
      indiaServers: "भारत सर्वर",
      privacyFirst: "गोपनीयता प्राथमिकता",
      freeBeta: "मुफ्त बीटा",
    },

    // Dashboard
    dashboard: {
      title: "डैशबोर्ड",
      overview: "अवलोकन",
      totalProducts: "कुल उत्पाद",
      totalInquiries: "कुल पूछताछ",
      activeLeads: "सक्रिय लीड्स",
      activeDeals: "सक्रिय सौदे",
      recentActivity: "हाल की गतिविधि",
      noActivity: "कोई हाल की गतिविधि नहीं",
      aiFeatures: "AI सुविधाएं",
      smartMatch: "AI स्मार्ट मैच",
      smartMatchDesc: "प्राकृतिक भाषा का उपयोग करके उत्पाद खोजें",
    },

    // Products
    products: {
      myProducts: "मेरे उत्पाद",
      productMarketplace: "उत्पाद मार्केटप्लेस",
      addProduct: "उत्पाद जोड़ें",
      addNewProduct: "नया उत्पाद जोड़ें",
      productImages: "उत्पाद छवियां",
      productTitle: "उत्पाद शीर्षक",
      description: "विवरण",
      category: "श्रेणी",
      price: "मूल्य",
      priceUSD: "मूल्य (USD)",
      minOrder: "न्यूनतम ऑर्डर",
      unit: "इकाई",
      originCountry: "मूल देश",
      hsCode: "HS कोड",
      specifications: "विनिर्देश",
      addSpecification: "विनिर्देश जोड़ें",
      aiGenerate: "AI जनरेट",
      generating: "जनरेट हो रहा है...",
      firstImageMain: "पहली छवि मुख्य प्रदर्शन छवि होगी",
      maxSizePerImage: "अधिकतम 5MB प्रति, JPG/PNG अनुशंसित",
      imagesAdded: "छवियां जोड़ी गईं",
      sendInquiry: "पूछताछ भेजें",
      verified: "सत्यापित",
      seller: "विक्रेता",
      manageListings: "अपनी उत्पाद सूची प्रबंधित करें",
      discoverProducts: "दुनिया भर के सत्यापित आपूर्तिकर्ताओं से उत्पाद खोजें",
    },

    // Inquiries
    inquiries: {
      myInquiries: "मेरी पूछताछ",
      receivedInquiries: "प्राप्त पूछताछ",
      trackInquiries: "अपनी उत्पाद पूछताछ ट्रैक करें और बातचीत जारी रखें",
      viewAndRespond: "खरीदार की पूछताछ देखें और जवाब दें",
      noInquiriesSent: "अभी तक कोई पूछताछ नहीं भेजी",
      noInquiriesReceived: "अभी तक कोई पूछताछ प्राप्त नहीं हुई",
      browseProducts: "शुरू करने के लिए उत्पाद ब्राउज़ करें और पूछताछ भेजें",
      addProductsToStart: "पूछताछ प्राप्त करना शुरू करने के लिए उत्पाद जोड़ें",
      viewConversation: "बातचीत देखें",
      initialMessage: "प्रारंभिक संदेश",
      quantity: "मात्रा",
      messages: "संदेश",
      typeMessage: "अपना संदेश लिखें...",
      send: "भेजें",
      sending: "भेज रहे हैं...",
      referenceImages: "संदर्भ छवियां (वैकल्पिक)",
      uploadUpTo3: "आप क्या खोज रहे हैं यह दिखाने के लिए 3 छवियां अपलोड करें",
      addReferenceImages: "संदर्भ छवियां जोड़ें",
    },

    // Leads
    leads: {
      title: "लीड पाइपलाइन",
      subtitle: "अपनी बिक्री पाइपलाइन प्रबंधित करें",
      stages: {
        new: "नया",
        contacted: "संपर्क किया",
        negotiation: "बातचीत",
        proposal: "प्रस्ताव",
        closedWon: "जीता",
        closedLost: "खोया",
      },
      noLeads: "अभी तक कोई लीड नहीं",
      value: "मूल्य",
      lastContact: "अंतिम संपर्क",
      moveStage: "स्टेज बदलें",
    },

    // AI Features
    ai: {
      smartMatch: "AI स्मार्ट मैच",
      smartMatchPlaceholder: "कोशिश करें: '4 टन बासमती चावल' या '5000 मीटर कॉटन कपड़ा'",
      findMatches: "मैच खोजें",
      matching: "मैच हो रहा है...",
      aiUnderstands: "AI मात्रा, गुणवत्ता समझता है और आपके लिए सर्वश्रेष्ठ विक्रेता खोजता है",
      smartMatchResults: "AI स्मार्ट मैच परिणाम",
      lookingFor: "खोज रहे हैं",
      clearShowAll: "साफ करें और सभी दिखाएं",
      matchScore: "मैच",
      recommended: "अनुशंसित",
      whyMatches: "यह क्यों मेल खाता है",
      moqFit: "MOQ",
      generateDescription: "विवरण जनरेट करें",
      aiSuggestions: "AI सुझाव",
      considerAddingSpecs: "ये विनिर्देश जोड़ने पर विचार करें",
      keywords: "कीवर्ड",
    },

    // Market Trends
    marketTrends: {
      title: "बाज़ार रुझान और इंटेलिजेंस",
      subtitle: "वैश्विक व्यापार अवसरों पर AI-संचालित अंतर्दृष्टि",
      lastUpdated: "अंतिम अपडेट",
      refreshTrends: "रुझान रीफ्रेश करें",
      analyzing: "वैश्विक बाज़ार रुझानों का विश्लेषण हो रहा है...",
      mayTakeFewSeconds: "इसमें कुछ सेकंड लग सकते हैं",
      marketOverview: "बाज़ार अवलोकन",
      trendingProducts: "ट्रेंडिंग उत्पाद",
      trendingDesc: "बढ़ती वैश्विक मांग वाले उत्पाद",
      marketAlerts: "बाज़ार अलर्ट",
      alertsDesc: "व्यापार को प्रभावित करने वाली महत्वपूर्ण खबरें",
      opportunities: "अवसर",
      opportunitiesDesc: "आपके व्यवसाय के लिए संभावित विकास क्षेत्र",
      riskAlerts: "जोखिम अलर्ट",
      risksDesc: "देखने योग्य संभावित चुनौतियां",
      priceUp: "मूल्य ऊपर",
      priceDown: "मूल्य नीचे",
      priceStable: "मूल्य स्थिर",
      score: "स्कोर",
      impact: "प्रभाव",
      highImpact: "उच्च प्रभाव",
      mediumImpact: "मध्यम प्रभाव",
      lowImpact: "कम प्रभाव",
      mitigation: "समाधान",
    },

    // Support
    support: {
      title: "संपर्क और सहायता",
      subtitle: "हम आपकी सफलता में मदद के लिए यहां हैं",
      whatsapp: "WhatsApp (सबसे तेज़)",
      email: "ईमेल",
      responseTime: "24 घंटे के भीतर जवाब",
      supportHours: "सहायता समय",
      faq: "अक्सर पूछे जाने वाले प्रश्न",
    },

    // Notifications
    notifications: {
      title: "सूचनाएं",
      markAllRead: "सभी पढ़ा हुआ चिह्नित करें",
      noNotifications: "कोई सूचना नहीं",
      newInquiry: "नई पूछताछ",
      newMessage: "नया संदेश",
      aiDraftReady: "AI ड्राफ्ट तैयार",
    },

    // Profile
    profile: {
      title: "प्रोफ़ाइल",
      personalInfo: "व्यक्तिगत जानकारी",
      companyInfo: "कंपनी जानकारी",
      verification: "सत्यापन",
      verificationStatus: "सत्यापन स्थिति",
      verified: "सत्यापित",
      pending: "लंबित",
      unverified: "असत्यापित",
    },

    // Settings
    settings: {
      title: "सेटिंग्स",
      knowledgeBase: "ज्ञान आधार",
      knowledgeBaseDesc: "AI को अपनी व्यावसायिक जानकारी से प्रशिक्षित करें",
      language: "भाषा",
      selectLanguage: "भाषा चुनें",
    },

    // Footer
    footer: {
      copyright: "© 2026 GlobalVyapar. वैश्विक व्यापार को जोड़ना।",
      privacyPolicy: "गोपनीयता नीति",
      termsOfService: "सेवा की शर्तें",
      refundPolicy: "रिफंड नीति",
    },

    // Toast Messages
    toast: {
      success: "सफल",
      error: "त्रुटि",
      loginSuccess: "वापसी पर स्वागत है!",
      registerSuccess: "खाता सफलतापूर्वक बनाया गया!",
      logoutSuccess: "सफलतापूर्वक लॉगआउट किया",
      productAdded: "उत्पाद सफलतापूर्वक जोड़ा गया",
      inquirySent: "पूछताछ सफलतापूर्वक भेजी गई!",
      messageSent: "संदेश भेजा गया",
      savedChanges: "परिवर्तन सहेजे गए",
      failedToLoad: "डेटा लोड करने में विफल",
      pleaseEnter: "कृपया दर्ज करें",
      invalidCredentials: "अमान्य क्रेडेंशियल्स",
    },
  },

  gu: {
    // Common
    common: {
      loading: "લોડ થઈ રહ્યું છે...",
      save: "સાચવો",
      cancel: "રદ કરો",
      submit: "સબમિટ કરો",
      delete: "કાઢી નાખો",
      edit: "સંપાદિત કરો",
      search: "શોધો",
      filter: "ફિલ્ટર",
      all: "બધા",
      yes: "હા",
      no: "ના",
      or: "અથવા",
      and: "અને",
      back: "પાછળ",
      next: "આગળ",
      close: "બંધ કરો",
      refresh: "રિફ્રેશ",
      viewAll: "બધું જુઓ",
      learnMore: "વધુ જાણો",
      getStarted: "શરૂ કરો",
    },

    // Navigation
    nav: {
      dashboard: "ડેશબોર્ડ",
      products: "ઉત્પાદનો",
      inquiries: "પૂછપરછ",
      leads: "લીડ્સ",
      aiDrafts: "AI ડ્રાફ્ટ્સ",
      trends: "ટ્રેન્ડ્સ",
      followUps: "ફોલો-અપ્સ",
      settings: "સેટિંગ્સ",
      profile: "પ્રોફાઇલ",
      logout: "લોગઆઉટ",
      login: "લોગિન",
      register: "રજિસ્ટર",
      marketplace: "માર્કેટપ્લેસ",
      support: "સહાય",
    },

    // Auth
    auth: {
      welcome: "સ્વાગત છે",
      signIn: "સાઇન ઇન કરો",
      signUp: "સાઇન અપ કરો",
      createAccount: "એકાઉન્ટ બનાવો",
      email: "ઇમેઇલ",
      password: "પાસવર્ડ",
      confirmPassword: "પાસવર્ડની પુષ્ટિ કરો",
      fullName: "પૂરું નામ",
      companyName: "કંપનીનું નામ",
      country: "દેશ",
      role: "હું છું",
      buyer: "ખરીદનાર (આયાતકાર)",
      seller: "વેચનાર (નિકાસકાર)",
      forgotPassword: "પાસવર્ડ ભૂલી ગયા?",
      resetPassword: "પાસવર્ડ રીસેટ કરો",
      newPassword: "નવો પાસવર્ડ",
      sendResetLink: "રીસેટ લિંક મોકલો",
      backToLogin: "લોગિન પર પાછા જાઓ",
      passwordResetSuccess: "પાસવર્ડ રીસેટ સફળ!",
      passwordResetSuccessMsg: "તમારો પાસવર્ડ રીસેટ થઈ ગયો છે. હવે તમે તમારા નવા પાસવર્ડથી લોગિન કરી શકો છો.",
      goToLogin: "લોગિન પર જાઓ",
      signingIn: "સાઇન ઇન થઈ રહ્યું છે...",
      creatingAccount: "એકાઉન્ટ બનાવી રહ્યા છીએ...",
      alreadyHaveAccount: "પહેલાથી એકાઉન્ટ છે?",
      dontHaveAccount: "એકાઉન્ટ નથી?",
      tagline: "જોડાઓ. ઓટોમેટ કરો. વૈશ્વિક વેપાર કરો.",
      valueProposition: "શું તમને 10+ પૂછપરછ/અઠવાડિયું મળે છે પણ <20% રૂપાંતરણ?",
      valuePropositionAnswer: "અમારી સિસ્ટમ આ ઠીક કરે છે.",
    },

    // Landing Page
    landing: {
      heroTitle: "AI-સંચાલિત B2B વેપાર પ્લેટફોર્મ",
      heroSubtitle: "વિશ્વભરના ચકાસાયેલા સપ્લાયર્સ અને ખરીદદારો સાથે જોડાઓ",
      exploreMarketplace: "માર્કેટપ્લેસ જુઓ",
      joinFree: "મફત જોડાઓ",
      trustedBy: "ભારતભરના વ્યવસાયો દ્વારા વિશ્વસનીય",
      features: "સુવિધાઓ",
      pricing: "કિંમત",
      freeDuringBeta: "બીટા દરમિયાન મફત",
      earlyAccessOpen: "અર્લી એક્સેસ ખુલ્લો છે",
      howItWorks: "આ કેવી રીતે કામ કરે છે",
      forBuyers: "ખરીદદારો માટે",
      forSellers: "વેચનારાઓ માટે",
      securityBadges: "તમારો ડેટા સુરક્ષિત છે",
      ssl256bit: "256-બિટ SSL",
      indiaServers: "ભારત સર્વર્સ",
      privacyFirst: "ગોપનીયતા પ્રથમ",
      freeBeta: "મફત બીટા",
    },

    // Dashboard
    dashboard: {
      title: "ડેશબોર્ડ",
      overview: "ઓવરવ્યૂ",
      totalProducts: "કુલ ઉત્પાદનો",
      totalInquiries: "કુલ પૂછપરછ",
      activeLeads: "સક્રિય લીડ્સ",
      activeDeals: "સક્રિય સોદા",
      recentActivity: "તાજેતરની પ્રવૃત્તિ",
      noActivity: "કોઈ તાજેતરની પ્રવૃત્તિ નથી",
      aiFeatures: "AI સુવિધાઓ",
      smartMatch: "AI સ્માર્ટ મેચ",
      smartMatchDesc: "કુદરતી ભાષાનો ઉપયોગ કરીને ઉત્પાદનો શોધો",
    },

    // Products
    products: {
      myProducts: "મારા ઉત્પાદનો",
      productMarketplace: "ઉત્પાદન માર્કેટપ્લેસ",
      addProduct: "ઉત્પાદન ઉમેરો",
      addNewProduct: "નવું ઉત્પાદન ઉમેરો",
      productImages: "ઉત્પાદન છબીઓ",
      productTitle: "ઉત્પાદન શીર્ષક",
      description: "વર્ણન",
      category: "શ્રેણી",
      price: "કિંમત",
      priceUSD: "કિંમત (USD)",
      minOrder: "ન્યૂનતમ ઓર્ડર",
      unit: "એકમ",
      originCountry: "મૂળ દેશ",
      hsCode: "HS કોડ",
      specifications: "વિશિષ્ટતાઓ",
      addSpecification: "વિશિષ્ટતા ઉમેરો",
      aiGenerate: "AI જનરેટ",
      generating: "જનરેટ થઈ રહ્યું છે...",
      firstImageMain: "પ્રથમ છબી મુખ્ય ડિસ્પ્લે છબી હશે",
      maxSizePerImage: "મહત્તમ 5MB દરેક, JPG/PNG ભલામણ",
      imagesAdded: "છબીઓ ઉમેરાઈ",
      sendInquiry: "પૂછપરછ મોકલો",
      verified: "ચકાસાયેલ",
      seller: "વેચનાર",
      manageListings: "તમારી ઉત્પાદન સૂચિ મેનેજ કરો",
      discoverProducts: "વિશ્વભરના ચકાસાયેલા સપ્લાયર્સ પાસેથી ઉત્પાદનો શોધો",
    },

    // Inquiries
    inquiries: {
      myInquiries: "મારી પૂછપરછ",
      receivedInquiries: "પ્રાપ્ત પૂછપરછ",
      trackInquiries: "તમારી ઉત્પાદન પૂછપરછ ટ્રેક કરો અને વાટાઘાટો ચાલુ રાખો",
      viewAndRespond: "ખરીદદારની પૂછપરછ જુઓ અને જવાબ આપો",
      noInquiriesSent: "હજુ સુધી કોઈ પૂછપરછ મોકલાઈ નથી",
      noInquiriesReceived: "હજુ સુધી કોઈ પૂછપરછ મળી નથી",
      browseProducts: "શરૂ કરવા માટે ઉત્પાદનો બ્રાઉઝ કરો અને પૂછપરછ મોકલો",
      addProductsToStart: "પૂછપરછ મેળવવાનું શરૂ કરવા માટે ઉત્પાદનો ઉમેરો",
      viewConversation: "વાતચીત જુઓ",
      initialMessage: "પ્રારંભિક સંદેશ",
      quantity: "જથ્થો",
      messages: "સંદેશાઓ",
      typeMessage: "તમારો સંદેશ લખો...",
      send: "મોકલો",
      sending: "મોકલી રહ્યા છીએ...",
      referenceImages: "સંદર્ભ છબીઓ (વૈકલ્પિક)",
      uploadUpTo3: "તમે શું શોધી રહ્યા છો તે બતાવવા 3 છબીઓ અપલોડ કરો",
      addReferenceImages: "સંદર્ભ છબીઓ ઉમેરો",
    },

    // Leads
    leads: {
      title: "લીડ પાઇપલાઇન",
      subtitle: "તમારી સેલ્સ પાઇપલાઇન મેનેજ કરો",
      stages: {
        new: "નવું",
        contacted: "સંપર્ક કર્યો",
        negotiation: "વાટાઘાટ",
        proposal: "દરખાસ્ત",
        closedWon: "જીત્યા",
        closedLost: "ગુમાવ્યા",
      },
      noLeads: "હજુ સુધી કોઈ લીડ્સ નથી",
      value: "મૂલ્ય",
      lastContact: "છેલ્લો સંપર્ક",
      moveStage: "સ્ટેજ બદલો",
    },

    // AI Features
    ai: {
      smartMatch: "AI સ્માર્ટ મેચ",
      smartMatchPlaceholder: "અજમાવો: '4 ટન બાસમતી ચોખા' અથવા '5000 મીટર કોટન કાપડ'",
      findMatches: "મેચ શોધો",
      matching: "મેચ થઈ રહ્યું છે...",
      aiUnderstands: "AI જથ્થો, ગુણવત્તા સમજે છે અને તમારા માટે શ્રેષ્ઠ વેચનાર શોધે છે",
      smartMatchResults: "AI સ્માર્ટ મેચ પરિણામો",
      lookingFor: "શોધી રહ્યા છો",
      clearShowAll: "સાફ કરો અને બધું બતાવો",
      matchScore: "મેચ",
      recommended: "ભલામણ કરેલ",
      whyMatches: "આ કેમ મેળ ખાય છે",
      moqFit: "MOQ",
      generateDescription: "વર્ણન જનરેટ કરો",
      aiSuggestions: "AI સૂચનો",
      considerAddingSpecs: "આ વિશિષ્ટતાઓ ઉમેરવાનું વિચારો",
      keywords: "કીવર્ડ્સ",
    },

    // Market Trends
    marketTrends: {
      title: "બજાર ટ્રેન્ડ્સ અને ઇન્ટેલિજન્સ",
      subtitle: "વૈશ્વિક વેપાર તકો પર AI-સંચાલિત આંતરદૃષ્ટિ",
      lastUpdated: "છેલ્લું અપડેટ",
      refreshTrends: "ટ્રેન્ડ્સ રિફ્રેશ કરો",
      analyzing: "વૈશ્વિક બજાર ટ્રેન્ડ્સનું વિશ્લેષણ થઈ રહ્યું છે...",
      mayTakeFewSeconds: "આમાં થોડી સેકન્ડ લાગી શકે છે",
      marketOverview: "બજાર ઓવરવ્યૂ",
      trendingProducts: "ટ્રેન્ડિંગ ઉત્પાદનો",
      trendingDesc: "વધતી વૈશ્વિક માંગ ધરાવતા ઉત્પાદનો",
      marketAlerts: "બજાર અલર્ટ્સ",
      alertsDesc: "વેપારને અસર કરતા મહત્વના સમાચાર",
      opportunities: "તકો",
      opportunitiesDesc: "તમારા વ્યવસાય માટે સંભવિત વૃદ્ધિ ક્ષેત્રો",
      riskAlerts: "જોખમ અલર્ટ્સ",
      risksDesc: "જોવા જેવા સંભવિત પડકારો",
      priceUp: "કિંમત ઉપર",
      priceDown: "કિંમત નીચે",
      priceStable: "કિંમત સ્થિર",
      score: "સ્કોર",
      impact: "અસર",
      highImpact: "ઉચ્ચ અસર",
      mediumImpact: "મધ્યમ અસર",
      lowImpact: "ઓછી અસર",
      mitigation: "ઉકેલ",
    },

    // Support
    support: {
      title: "સંપર્ક અને સહાય",
      subtitle: "અમે તમારી સફળતામાં મદદ કરવા અહીં છીએ",
      whatsapp: "WhatsApp (સૌથી ઝડપી)",
      email: "ઇમેઇલ",
      responseTime: "24 કલાકની અંદર જવાબ",
      supportHours: "સહાય સમય",
      faq: "વારંવાર પૂછાતા પ્રશ્નો",
    },

    // Notifications
    notifications: {
      title: "સૂચનાઓ",
      markAllRead: "બધું વાંચેલું ચિહ્નિત કરો",
      noNotifications: "કોઈ સૂચનાઓ નથી",
      newInquiry: "નવી પૂછપરછ",
      newMessage: "નવો સંદેશ",
      aiDraftReady: "AI ડ્રાફ્ટ તૈયાર",
    },

    // Profile
    profile: {
      title: "પ્રોફાઇલ",
      personalInfo: "વ્યક્તિગત માહિતી",
      companyInfo: "કંપની માહિતી",
      verification: "ચકાસણી",
      verificationStatus: "ચકાસણી સ્થિતિ",
      verified: "ચકાસાયેલ",
      pending: "બાકી",
      unverified: "અચકાસાયેલ",
    },

    // Settings
    settings: {
      title: "સેટિંગ્સ",
      knowledgeBase: "નોલેજ બેઝ",
      knowledgeBaseDesc: "AI ને તમારી વ્યવસાય માહિતીથી તાલીમ આપો",
      language: "ભાષા",
      selectLanguage: "ભાષા પસંદ કરો",
    },

    // Footer
    footer: {
      copyright: "© 2026 GlobalVyapar. વૈશ્વિક વેપારને જોડવું.",
      privacyPolicy: "ગોપનીયતા નીતિ",
      termsOfService: "સેવાની શરતો",
      refundPolicy: "રિફંડ નીતિ",
    },

    // Toast Messages
    toast: {
      success: "સફળ",
      error: "ભૂલ",
      loginSuccess: "પાછા આવવા બદલ આભાર!",
      registerSuccess: "એકાઉન્ટ સફળતાપૂર્વક બનાવ્યું!",
      logoutSuccess: "સફળતાપૂર્વક લોગઆઉટ થયું",
      productAdded: "ઉત્પાદન સફળતાપૂર્વક ઉમેરાયું",
      inquirySent: "પૂછપરછ સફળતાપૂર્વક મોકલાઈ!",
      messageSent: "સંદેશ મોકલાયો",
      savedChanges: "ફેરફારો સાચવ્યા",
      failedToLoad: "ડેટા લોડ કરવામાં નિષ્ફળ",
      pleaseEnter: "કૃપા કરીને દાખલ કરો",
      invalidCredentials: "અમાન્ય ઓળખપત્રો",
    },
  },
};

// Language metadata
export const languageOptions = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી', flag: '🇮🇳' },
];

// Helper to get browser language
export const getBrowserLanguage = () => {
  const browserLang = navigator.language?.split('-')[0] || 'en';
  const supportedLangs = ['en', 'hi', 'gu'];
  return supportedLangs.includes(browserLang) ? browserLang : 'en';
};
